import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, AlertTriangle, Clock, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_MAP = {
  succeeded:            { icon: CheckCircle2, color: 'text-emerald-500', bg: 'bg-emerald-50', i18nKey: 'succeeded' },
  failed:               { icon: XCircle,      color: 'text-red-500',    bg: 'bg-red-50',     i18nKey: 'failed' },
  cancelled:            { icon: AlertTriangle,color: 'text-amber-500',  bg: 'bg-amber-50',   i18nKey: 'cancelled' },
  pending_verification: { icon: Clock,        color: 'text-blue-500',   bg: 'bg-blue-50',    i18nKey: 'pendingVerification' },
  unknown:              { icon: AlertTriangle,color: 'text-gray-500',   bg: 'bg-gray-50',    i18nKey: 'unknown' },
  error:                { icon: XCircle,      color: 'text-red-500',    bg: 'bg-red-50',     i18nKey: 'error' },
};

export default function PaymentResult() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const status = params.get('status') || 'unknown';
  const invoice = params.get('invoice') || '';
  const errorMsg = params.get('error') || '';
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(!!invoice);

  useEffect(() => {
    if (!invoice) {
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/api/public/vinti4/result/${invoice}`)
      .then((r) => setDetails(r.data))
      .catch(() => setDetails(null))
      .finally(() => setLoading(false));
  }, [invoice]);

  const meta = STATUS_MAP[status] || STATUS_MAP.unknown;
  const Icon = meta.icon;

  const statusValueLabel = (s) => (
    s === 'approved' ? t('paymentResult.statusApproved')
    : s === 'cancelled' ? t('paymentResult.statusCancelled')
    : s === 'failed' ? t('paymentResult.statusFailed')
    : s === 'pending' ? t('paymentResult.statusPending')
    : s
  );

  return (
    <div className="min-h-screen bg-gray-50 py-16 px-4">
      <div className="max-w-xl mx-auto">
        <Card className="p-8 text-center" data-testid="payment-result-card">
          {loading ? (
            <div className="py-12">
              <Loader2 className="h-10 w-10 animate-spin mx-auto text-blue-500" />
              <p className="mt-3 text-gray-600">{t('paymentResult.checkingStatus')}</p>
            </div>
          ) : (
            <>
              <div className={`w-20 h-20 ${meta.bg} rounded-full mx-auto flex items-center justify-center mb-6`}>
                <Icon className={`h-12 w-12 ${meta.color}`} />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-3" data-testid="payment-result-title">
                {t(`paymentResult.title.${meta.i18nKey}`)}
              </h1>
              <p className="text-gray-600 mb-6">{t(`paymentResult.description.${meta.i18nKey}`)}</p>

              {details && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left text-sm">
                  <Row label={t('paymentResult.invoice')} value={details.invoice_number} />
                  {details.plan_name && <Row label={t('paymentResult.plan')} value={details.plan_name} />}
                  {details.amount_cve != null && (
                    <Row label={t('paymentResult.amount')} value={`${details.amount_cve.toLocaleString('pt-PT')} CVE`} />
                  )}
                  <Row label={t('paymentResult.status')} value={statusValueLabel(details.status)} />
                </div>
              )}

              {errorMsg && (
                <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded p-3 mb-6">
                  <strong>{t('paymentResult.technicalDetail')}:</strong> {errorMsg}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {status === 'succeeded' ? (
                  <Button
                    onClick={() => navigate('/dashboard')}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="payment-result-dashboard-btn"
                  >
                    {t('paymentResult.goToDashboard')}
                  </Button>
                ) : (
                  <Button
                    onClick={() => navigate('/checkout')}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="payment-result-retry-btn"
                  >
                    {t('paymentResult.tryAgain')}
                  </Button>
                )}
                <Button variant="outline" asChild>
                  <Link to="/purchases" data-testid="payment-result-history-btn">{t('paymentResult.viewHistory')}</Link>
                </Button>
              </div>
            </>
          )}
        </Card>

        <p className="text-center text-xs text-gray-500 mt-6">
          {t('paymentResult.contactSupport')} <a href="mailto:info@trabadju.cv" className="text-blue-600 underline">info@trabadju.cv</a>.
        </p>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between py-1.5 border-b border-gray-200 last:border-b-0">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-900">{value}</span>
    </div>
  );
}
